import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "config/axiosConfig";
import ModalOrDrawer from "ui/ModalOrDrawer";
import InputField from "ui/InputField";
import { getAccountSettingsUiStrings, resolveAccountSettingsLocale } from "i18n/accountSettingsUi";
import { AuthSession } from "util/constants";
import { apiRoutes } from "util/apiRoutes";
import { normalizeApiError } from "util/normalizeApiError";

const formatSessionTimestamp = (value?: string | null): string => {
    if (!value) {
        return "";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString();
};

const buildSessionSummary = (session: AuthSession, unknownDeviceLabel: string): string => {
    const browserSummary = [session.browser_name, session.os_name].filter(Boolean).join(" on ");
    return browserSummary || session.device_type || unknownDeviceLabel;
};

const buildSessionLocation = (session: AuthSession, unknownLocationLabel: string): string => {
    const location = [session.geo_region, session.geo_country].filter(Boolean).join(", ");
    return location || unknownLocationLabel;
};

const UserSettingsAccountSessions: React.FC = () => {
    const ui = getAccountSettingsUiStrings(resolveAccountSettingsLocale());
    const [sessions, setSessions] = useState<AuthSession[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRevokeAllOpen, setIsRevokeAllOpen] = useState(false);
    const [revokeAllPassword, setRevokeAllPassword] = useState("");
    const [isRevokingAll, setIsRevokingAll] = useState(false);

    const loadSessions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get<{ sessions: AuthSession[] }>(apiRoutes.auth.sessions);
            setSessions(Array.isArray(response.data?.sessions) ? response.data.sessions : []);
        } catch (requestError: unknown) {
            setError(normalizeApiError(requestError, ui.sessionsLoadError));
        } finally {
            setIsLoading(false);
        }
    }, [ui.sessionsLoadError]);

    useEffect(() => {
        loadSessions().catch(() => undefined);
    }, [loadSessions]);

    const otherSessions = useMemo(
        () => sessions.filter((session) => !session.current),
        [sessions]
    );

    return (
        <div className="form-control space-y-2">
            <label className="form-label text-lg">{ui.sessionsSection}</label>
            <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {ui.sessionsDescription}
            </p>

            {isLoading ? (
                <div className="rounded-md bg-eoe-bg-hover p-3 text-sm text-eoe-text-sub dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text-sub">
                    {ui.sessionsDescription}
                </div>
            ) : null}

            {error ? (
                <div className="rounded-md bg-eoe-bg-hover p-3 text-sm text-warning-red dark:bg-eoe-dark-bg-hover dark:text-warning-red-dark">
                    {error}
                </div>
            ) : null}

            {sessions.length <= 1 && !isLoading ? (
                <div className="rounded-md bg-eoe-bg-hover p-3 text-sm text-eoe-text-sub dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text-sub">
                    {ui.sessionsEmpty}
                </div>
            ) : null}

            {sessions.map((session) => (
                <div
                    key={session.id}
                    className="flex items-start justify-between gap-4 rounded-md bg-eoe-bg-hover p-3 dark:bg-eoe-dark-bg-hover"
                >
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
                                {buildSessionSummary(session, ui.sessionsUnknownDevice)}
                            </span>
                            {session.current ? (
                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                    {ui.sessionsCurrentBadge}
                                </span>
                            ) : null}
                        </div>
                        <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {buildSessionLocation(session, ui.sessionsUnknownLocation)}
                            {session.ip_address ? ` • ${session.ip_address}` : ""}
                        </span>
                        <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {`${ui.sessionsLastActivePrefix}: ${formatSessionTimestamp(session.last_used_at || session.created_at)}`}
                        </span>
                    </div>
                    {!session.current ? (
                        <button
                            type="button"
                            className="btn-secondary px-4 py-2 text-xs"
                            onClick={async () => {
                                setError(null);
                                try {
                                    await api.delete(apiRoutes.auth.sessionById(session.id));
                                    await loadSessions();
                                } catch (requestError: unknown) {
                                    setError(normalizeApiError(requestError, ui.sessionsRevokeError));
                                }
                            }}
                        >
                            {ui.sessionsSignOutAction}
                        </button>
                    ) : null}
                </div>
            ))}

            {otherSessions.length > 0 ? (
                <button
                    type="button"
                    className="btn-primary w-full sm:w-fit py-2 px-4"
                    onClick={() => setIsRevokeAllOpen(true)}
                >
                    {ui.sessionsSignOutOtherAction}
                </button>
            ) : null}

            <ModalOrDrawer
                isOpen={isRevokeAllOpen}
                onClose={() => {
                    setIsRevokeAllOpen(false);
                    setRevokeAllPassword("");
                }}
                title={ui.sessionsRevokeAllTitle}
                stickyFooter
                footer={
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            className="btn-secondary w-full sm:w-fit py-2 px-4"
                            onClick={() => {
                                setIsRevokeAllOpen(false);
                                setRevokeAllPassword("");
                            }}
                        >
                            {ui.modalCancel}
                        </button>
                        <button
                            type="button"
                            className="btn-primary w-full sm:w-fit py-2 px-4"
                            disabled={isRevokingAll || revokeAllPassword.trim().length === 0}
                            onClick={async () => {
                                setIsRevokingAll(true);
                                setError(null);
                                try {
                                    await api.delete(apiRoutes.auth.sessions, {
                                        data: {
                                            currentPassword: revokeAllPassword,
                                        },
                                    });
                                    setIsRevokeAllOpen(false);
                                    setRevokeAllPassword("");
                                    await loadSessions();
                                } catch (requestError: unknown) {
                                    setError(normalizeApiError(requestError, ui.sessionsRevokeAllError));
                                } finally {
                                    setIsRevokingAll(false);
                                }
                            }}
                        >
                            {ui.sessionsRevokeAllConfirm}
                        </button>
                    </div>
                }
            >
                <div className="flex flex-col gap-4 py-2 text-center">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {ui.sessionsRevokeAllDescription}
                    </p>
                    <InputField
                        type="password"
                        id="revokeAllSessionsPassword"
                        name="revokeAllSessionsPassword"
                        labelText={ui.sessionsRevokeAllPasswordLabel}
                        autoComplete="current-password"
                        fieldRequired
                        value={revokeAllPassword}
                        onChange={(event) => setRevokeAllPassword(event.target.value)}
                        spellCheck={false}
                    />
                </div>
            </ModalOrDrawer>
        </div>
    );
};

export default UserSettingsAccountSessions;
